import { useEffect } from "react"
import { useMyTheme } from "../../ThemeProviderWrapper";
import { createTheme, useMediaQuery } from "@mui/material";
import { getEntityThemedCtaColors } from "../../utils/ctaContrast.utils";
const SetTheme = ({ setTheme, customDetails }) => {
  const { changeTheme } = useMyTheme();
  const mobileScreen = useMediaQuery('(max-width:900px)');
  
  useEffect(() => {
    // Check if dark theme is enabled
    const isDarkTheme = customDetails?.darkTheme === true;
    const { backgroundColor, hoverBackgroundColor, color } =
      getEntityThemedCtaColors(customDetails, isDarkTheme);

    // Set data attribute for CSS variables
    document.documentElement.setAttribute('data-theme', isDarkTheme ? 'dark' : 'light');
    
    let newTheme = createTheme({
      palette: {
        mode: isDarkTheme ? 'dark' : 'light',
        primary: {
          light: customDetails.secondaryColor,
          main: customDetails.primaryColor,
          white: "#FFFFFF",
        },
        secondary: {
          main: customDetails.secondaryColor,
          light: "#E3E9EE"
        },
        error: {
          main: "#C62828",
        },
        success: {
          main: "#20BE79",
          light: "#4de6a3",
        },
        info: {
          main: isDarkTheme ? "#FFFFFF" : "#000000",
          light: isDarkTheme ? "#161616" : "#F3F5FD",
        },
        textColor: customDetails.textColor,
        black60: isDarkTheme ? "#9AA3A8" : "#5F6368",
        black40: isDarkTheme ? "#A5AAB8" : "#6B7280",
        backgroundMobileSubHeader: isDarkTheme ? "#161616" : "#2B3056",
        textBlack: isDarkTheme ? "#FFFFFF" : "#111111",
        tagRed: {
          main: "#CA2626",
          light: "#FDEDED",
        },
        // Dark theme specific colors
        background: {
          default: isDarkTheme ? "#161616" : "#FFFFFF",
          paper: isDarkTheme ? "#161616" : "#FFFFFF",
        },
        text: {
          primary: customDetails.primaryTextColor || (isDarkTheme ? "#FFFFFF" : "#000000"),
          secondary: customDetails.secondaryTextColor || (isDarkTheme ? "#9AA3A8" : "#5F6368"),
        },
      },
      typography: {
        fontFamily: customDetails.fontFamily
      },
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderRadius: "14px",
              borderColor: isDarkTheme ? customDetails.borderColor || "#757575" : "#757575",
            },
            root: {
              '& label': {
                color: customDetails.primaryColor,
              },
              '& label.Mui-focused': {
                color: customDetails.primaryColor,
              },
              '& .MuiInput-underline:after': {
                borderBottomColor: customDetails.primaryColor,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: customDetails.primaryColor,
                },
                '&:hover fieldset': {
                  borderColor: customDetails.primaryColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: customDetails.primaryColor,
                },
            }
          },
        },
      },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: customDetails.borderRadius,
            },
            contained: {
              backgroundColor,
              color,
              '&:hover': {
                backgroundColor: hoverBackgroundColor,
              },
              '&.Mui-disabled': {
                backgroundColor: isDarkTheme ? '#7A8185' : '#E0E0E0',
                color: isDarkTheme ? '#161616' : '#757575',
              },
            },
            containedPrimary: {
              backgroundColor,
              color,
              '&:hover': {
                backgroundColor: hoverBackgroundColor,
              },
              '&.Mui-disabled': {
                backgroundColor: isDarkTheme ? '#7A8185' : '#E0E0E0',
                color: isDarkTheme ? '#161616' : '#757575',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkTheme ? "#161616" : "#FFFFFF",
              color: isDarkTheme ? "#FFFFFF" : "#000000",
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              backgroundColor: isDarkTheme ? "#161616" : "#FFFFFF",
              color: isDarkTheme ? "#9AA3A8" : "#5F6368",
              borderColor: isDarkTheme ? "#757575" : "#757575",
            },
          },
        },
        MuiListItemAvatar: {
          styleOverrides: {
            root: {
              minWidth: "unset",
            }
          }
        },
        MuiFab: {
          styleOverrides: {
            root: {
              boxShadow: "unset",
              transition: "unset"
            }
          }
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              minWidth: "unset",
              paddingRight: "0.25rem"
            }
          }
        },
        MuiTypography: {
          styleOverrides: {
            root: {
              fontSize: mobileScreen ? "1rem" : "0.875rem"
            }
          }
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: isDarkTheme ? '#9AA3A8' : '#757575',
              '&.Mui-checked': {
                color: customDetails.primaryColor || '#3053D3',
              },
              '&.Mui-disabled': {
                color: isDarkTheme ? '#757575' : '#949494',
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              '&::placeholder': {
                color: isDarkTheme ? '#9AA3A8' : '#757575',
                opacity: 1,
              },
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              marginLeft: 0,
              lineHeight: 1.5,
            },
          },
        },
      },
    });
    changeTheme(newTheme);
    setTheme(newTheme)
    return () => null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDetails])

  return null
}

export default SetTheme;